import { api, ApiError, upload } from './api';

interface SendOptions {
  projectId: string;
  purpose: 'item' | 'version' | 'file';
  file: File;
  itemId?: string;
  title?: string;
  note?: string;
  /** Files above this size are sent in pieces (from /api/meta). */
  chunkBytes: number;
  onProgress?: (fraction: number) => void;
}

function putChunk(url: string, blob: Blob, onProgress: (loaded: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (event) => onProgress(event.loaded);
    xhr.onerror = () => reject(new ApiError(0, 'The upload was interrupted.', 'network'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let message = `Upload failed (${xhr.status}).`;
      try {
        message = JSON.parse(xhr.responseText).error || message;
      } catch {
        /* keep the generic message */
      }
      reject(new ApiError(xhr.status, message));
    };
    xhr.send(blob);
  });
}

/**
 * Send one file to a project. Small files go up in a single request; large
 * ones (video) are split into chunks so they pass proxies with body-size
 * limits and survive a flaky connection: each chunk is retried on its own.
 */
export async function sendFile<T>({ projectId, purpose, file, itemId, title, note, chunkBytes, onProgress }: SendOptions): Promise<T> {
  if (file.size <= chunkBytes) {
    const form = new FormData();
    if (title) form.append('title', title);
    if (note) form.append('note', note);
    const url = purpose === 'item' ? `/api/projects/${projectId}/items` : purpose === 'version' ? `/api/items/${itemId}/versions` : `/api/projects/${projectId}/files`;
    form.append(purpose === 'file' ? 'files' : 'file', file);
    return upload<T>(url, form, onProgress);
  }

  const started = await api.post<{ uploadId: string; chunkSize: number; chunks: number }>(`/api/projects/${projectId}/uploads`, {
    purpose,
    filename: file.name,
    size: file.size,
    mime: file.type,
    ...(itemId ? { itemId } : {}),
    ...(title ? { title } : {}),
    ...(note ? { note } : {}),
  });

  try {
    for (let index = 0; index < started.chunks; index += 1) {
      const begin = index * started.chunkSize;
      const blob = file.slice(begin, begin + started.chunkSize);
      let attempt = 0;
      for (;;) {
        try {
          await putChunk(`/api/uploads/${started.uploadId}/chunks/${index}`, blob, (loaded) => onProgress?.(Math.min(0.99, (begin + loaded) / file.size)));
          break;
        } catch (error) {
          attempt += 1;
          // Only network drops are worth retrying; a 4xx will fail the same way again.
          if (attempt >= 3 || !(error instanceof ApiError) || error.status !== 0) throw error;
          await new Promise((resolve) => setTimeout(resolve, 1500 * attempt));
        }
      }
    }
    const result = await api.post<T>(`/api/uploads/${started.uploadId}/complete`, {});
    onProgress?.(1);
    return result;
  } catch (error) {
    void api.del(`/api/uploads/${started.uploadId}`).catch(() => undefined);
    throw error;
  }
}
